// Informes por puesto, procedimientos y medidas (lote D, M49): lectura y escritura con supabase-js.
// Lee lo que YA guardan los módulos (puestos, trabajadores, evaluaciones, Matriz de Riesgos, Incidentes, permisos) y
// escribe solo en las tablas de M49. Las URL de los archivos son firmadas por 120 s (bucket privado sst-informes).

import { supabase } from '../../supabaseClient';
import type { HygieneAnswers, HygieneResponse } from '../biogaval';
import { conLista, type ContenidoSocializacion } from '../firmas/firmas-tipos';
import { cedulaValida } from '../firmas/firmas-logica';
import { nombreSeguroStorage as aRutaSegura } from '../nombre-storage';
import {
  evaluacionEstres, evaluacionesBiogaval, evaluacionIluminacion, evaluacionIper, evaluacionNiosh, evaluacionPsicosocial, evaluacionQuimico,
  evaluacionReba, evaluacionRosa, evaluacionRula, evaluacionRuido, evaluacionSnook, type FilaBioExposicion, type FilaIper, type FilaModulo,
  type FilaQuimico,
} from './ip-evaluaciones';
import {
  ITEMS_INFORMES, ITEM_PRIORITARIA, ITEM_PROCEDIMIENTOS, ITEM_VERIFICACION, escribirLoteD, estadoPuestos, resultadoItemInformes,
  resultadoPrioritaria, resultadoProcedimientos, resultadoVerificacionGT9, type EntradaPuesto, type MedidaGT9, type PermisoMin,
  type ResultadoItemLoteD, type ResumenPrioritaria,
} from './ip-checklist';
import { evaluacionesDeTipo, huellaEvaluaciones, medidaVista, ultimaVersion, type AccionIncidente, type IperConMedidas } from './ip-logica';
import type { FilaProcedimiento, PreguntaPermiso, TipoPermiso } from './ip-procedimientos';
import {
  BUCKET_INFORMES, TIPOS_INFORME, type Evaluacion, type FilaAdjunto, type FilaInforme, type FilaMedida, type FilaVerificacion, type FilaVersion,
  type MedidaVista, type ResultadoVerificacion, type TipoInforme,
} from './ip-tipos';
import type { TrazasPlanV2 } from '../plan-emergencias-compliance-sync';

const msgError = (e: { message?: string; code?: string } | null | undefined, accion: string): string => {
  const m = e?.message ?? '';
  if (e?.code === '42501' || /row-level security|permission denied/i.test(m)) return `No tienes permiso para ${accion}.`;
  if (/relation .* does not exist|schema cache|PGRST20[45]/i.test(m)) return 'Esta función todavía no está disponible para tu empresa.';
  if (/[áéíóúñ]/i.test(m) || /^(El|La|Un|Una|Falta|Solo)\b/.test(m)) return m.replace(/\.?$/, '.');
  return `No se pudo ${accion}. Intenta de nuevo.`;
};
const falla = (e: { message?: string; code?: string } | null, accion: string): never => { throw new Error(msgError(e, accion)); };

// ── Lectura de todo lo de la empresa ──
export interface PuestoBase { id: string; name: string }
export interface DatosEmpresa {
  puestos: PuestoBase[];
  activosPorPuesto: Record<string, number>;
  evaluacionesPorPuesto: Record<string, Evaluacion[]>;
  informes: FilaInforme[];
  versiones: FilaVersion[];
  adjuntos: FilaAdjunto[];
  medidas: FilaMedida[];
  verificaciones: FilaVerificacion[];
  iper: IperConMedidas[];
  acciones: AccionIncidente[];
}

const lista = async <T>(q: PromiseLike<{ data: unknown; error: { message?: string } | null }>): Promise<T[]> => {
  const { data, error } = await q;
  return error ? [] : ((data ?? []) as T[]);
};

export interface FilaResultadoPsico { assessment_id: string; unit_name: string; respondents: number; suppressed: boolean;
  distributions: { global?: { bajo: number; medio: number; alto: number } } | null }

/**
 * Resultados psicosociales de las evaluaciones dadas. psychosocial_results no tiene company_id: cuelga de la evaluación
 * (assessment_id) y su RLS mira la empresa de la evaluación. Se filtra por los ids de las evaluaciones cerradas de la
 * empresa (filtrar por company_id daba 400).
 */
export async function cargarResultadosPsico(assessmentIds: string[]): Promise<FilaResultadoPsico[]> {
  if (!assessmentIds.length) return [];
  return lista<FilaResultadoPsico>(supabase.from('psychosocial_results')
    .select('assessment_id, unit_name, respondents, suppressed, distributions').in('assessment_id', assessmentIds));
}

/** Evaluaciones y mediciones de cada puesto, con lo que el informe necesita (cada módulo con su referencia). */
export async function cargarEvaluaciones(companyId: string, puestos: PuestoBase[]): Promise<Record<string, Evaluacion[]>> {
  const de = (tabla: string, cols = '*') => lista<FilaModulo>(supabase.from(tabla).select(cols).eq('company_id', companyId));
  const [reba, rula, rosa, niosh, snook, ruido, luz, calor, iper, quim, tareas, actividades, bio, hig, psico] = await Promise.all([
    de('reba_assessments'), de('rula_assessments'), de('rosa_assessments'), de('niosh_assessments'), de('snook_ciriello_assessments'),
    de('noise_calculations'), de('lighting_calculations'), de('heat_stress_calculations'),
    lista<FilaIper>(supabase.from('iper_assessments').select('*').eq('company_id', companyId)),
    lista<FilaQuimico & { task_id: string | null }>(supabase.from('task_chemical_assessments')
      .select('id, task_id, product_name_snapshot, nivel_riesgo, accion_recomendada, created_at').eq('company_id', companyId)),
    lista<{ id: string; activity_id: string }>(supabase.from('activity_tasks').select('id, activity_id').eq('company_id', companyId)),
    lista<{ id: string; position_id: string | null }>(supabase.from('process_activities').select('id, position_id').eq('company_id', companyId)),
    lista<FilaBioExposicion>(supabase.from('job_position_biological_exposure')
      .select('id, job_position_id, agent_id, via_probabilidad, valor_probabilidad, frecuencia_pct, vacunacion_pct, created_at, biological_agents(name, grupo, vias_transmision)')
      .eq('company_id', companyId)),
    lista<{ job_position_id: string; item_id: string; respuesta: HygieneResponse; updated_at: string | null }>(supabase.from('job_position_biological_hygiene')
      .select('job_position_id, item_id, respuesta, updated_at').eq('company_id', companyId)),
    lista<{ id: string; title: string; status: string; closed_at: string | null }>(supabase.from('psychosocial_assessments')
      .select('id, title, status, closed_at').eq('company_id', companyId).eq('status', 'cerrada')),
  ]);
  const psicoRes = await cargarResultadosPsico(psico.map(a => a.id));
  const posDeActividad = new Map(actividades.map(a => [a.id, a.position_id]));
  const posDeTarea = new Map(tareas.map(t => [t.id, posDeActividad.get(t.activity_id) ?? null]));
  const out: Record<string, Evaluacion[]> = Object.fromEntries(puestos.map(p => [p.id, [] as Evaluacion[]]));
  const poner = (pid: string | null | undefined, e: Evaluacion) => { if (pid && out[pid]) out[pid].push(e); };
  reba.forEach(f => poner(f.job_position_id, evaluacionReba(f)));
  rula.forEach(f => poner(f.job_position_id, evaluacionRula(f)));
  rosa.forEach(f => poner(f.job_position_id, evaluacionRosa(f)));
  niosh.forEach(f => poner(f.job_position_id, evaluacionNiosh(f)));
  snook.forEach(f => poner(f.job_position_id, evaluacionSnook(f)));
  ruido.forEach(f => poner(f.job_position_id, evaluacionRuido(f)));
  luz.forEach(f => poner(f.job_position_id, evaluacionIluminacion(f)));
  calor.forEach(f => poner(f.job_position_id, evaluacionEstres(f)));
  iper.forEach(f => poner(f.position_id, evaluacionIper(f)));
  quim.forEach(f => poner(f.task_id ? posDeTarea.get(f.task_id) : null, evaluacionQuimico({ ...f, position_id: null })));
  for (const p of puestos) {
    const exps = bio.filter(e => e.job_position_id === p.id);
    if (exps.length) {
      const h = hig.filter(x => x.job_position_id === p.id);
      const respuestas: HygieneAnswers = Object.fromEntries(h.map(x => [x.item_id, x.respuesta]));
      const cambio = h.map(x => x.updated_at ?? '').sort().pop() || null;
      out[p.id].push(...evaluacionesBiogaval(exps, respuestas, cambio));
    }
    const nombre = p.name.trim().toLowerCase();
    for (const a of psico) {
      const r = psicoRes.find(x => x.assessment_id === a.id && x.unit_name.trim().toLowerCase() === nombre);
      if (!r) continue;   // solo si una unidad de la evaluación se llama como el puesto
      const g = r.distributions?.global;
      out[p.id].push(evaluacionPsicosocial({ id: a.id, titulo: a.title, cerrada: a.closed_at ?? '',
        unidad: !r.suppressed && g ? { encuestados: r.respondents, bajo: g.bajo, medio: g.medio, alto: g.alto } : null }));
    }
  }
  return out;
}

export async function cargarDatosEmpresa(companyId: string): Promise<DatosEmpresa> {
  const { data: pos, error } = await supabase.from('job_positions').select('id, name').eq('company_id', companyId).order('name');
  if (error) falla(error, 'leer los puestos de trabajo');
  const puestos = (pos ?? []) as PuestoBase[];
  const [workers, informes, versiones, adjuntos, medidas, verificaciones, iper, acciones, incidentes, evaluacionesPorPuesto] = await Promise.all([
    lista<{ position_id: string | null; status: string | null }>(supabase.from('workers').select('position_id, status').eq('company_id', companyId)),
    lista<FilaInforme>(supabase.from('position_reports').select('*').eq('company_id', companyId)),
    lista<FilaVersion>(supabase.from('sst_document_versions').select('*').eq('company_id', companyId).eq('doc_type', 'informe_puesto')),
    lista<FilaAdjunto>(supabase.from('position_report_files').select('*').eq('company_id', companyId)),
    lista<FilaMedida>(supabase.from('position_measures').select('*').eq('company_id', companyId)),
    lista<FilaVerificacion>(supabase.from('position_measure_verifications').select('*').eq('company_id', companyId)),
    lista<IperConMedidas>(supabase.from('iper_assessments').select('id, position_id, task, hazard_type, medida_eliminacion, medida_sustitucion, medida_control_ingenieria, medida_control_administrativo, medida_epp').eq('company_id', companyId)),
    lista<Omit<AccionIncidente, 'position_id'> & { incident_id: string }>(supabase.from('incident_actions')
      .select('id, incident_id, description, control_level, responsible_name, due_date, follow_up, status, completed_at, closed_at, evidence_name, evidence_path').eq('company_id', companyId)),
    lista<{ id: string; position_id: string | null }>(supabase.from('incidents').select('id, position_id').eq('company_id', companyId)),
    cargarEvaluaciones(companyId, puestos),
  ]);
  const activosPorPuesto: Record<string, number> = {};
  workers.filter(w => (w.status ?? 'activo') === 'activo' && w.position_id).forEach(w => { activosPorPuesto[w.position_id!] = (activosPorPuesto[w.position_id!] ?? 0) + 1; });
  const posInc = new Map(incidentes.map(i => [i.id, i.position_id]));
  return { puestos, activosPorPuesto, evaluacionesPorPuesto, informes, versiones, adjuntos, medidas, verificaciones, iper,
    acciones: acciones.map(a => ({ ...a, position_id: posInc.get(a.incident_id) ?? null })) };
}

export function medidasDelPuesto(d: DatosEmpresa, puestoId: string): MedidaVista[] {
  const iper = new Map(d.iper.map(i => [i.id, i]));
  const acc = new Map(d.acciones.map(a => [a.id, a]));
  return d.medidas.filter(m => m.job_position_id === puestoId).map(m => medidaVista(m, iper, acc));
}

/** GT 9: todas las medidas de la empresa con su estado resuelto desde su origen. */
export function medidasGT9(d: DatosEmpresa): MedidaGT9[] {
  const iper = new Map(d.iper.map(i => [i.id, i]));
  const acc = new Map(d.acciones.map(a => [a.id, a]));
  return d.medidas.map(m => {
    const v = medidaVista(m, iper, acc);
    return { id: m.id, puesto_id: m.job_position_id, estado: v.estado, cerrada_el: v.cerrada_el, creada: m.created_at ?? '', descripcion: v.descripcion };
  });
}
/** GT 9: ids de las medidas de la última versión aprobada de cada informe de medidas; null si no hay ninguno aprobado. */
export function idsMedidasAprobadas(d: DatosEmpresa): Set<string> | null {
  const ultimas = d.informes.filter(i => i.kind === 'medidas').map(i => ultimaVersion(d.versiones.filter(v => v.report_id === i.id))).filter(Boolean) as FilaVersion[];
  if (!ultimas.length) return null;
  return new Set(ultimas.flatMap(v => ((v.snapshot as { medidas?: { id: string }[] }).medidas ?? []).map(m => m.id)));
}

/** Evaluaciones de cada informe del puesto. GT 7 usa todas las del puesto (cambian = el informe de medidas se revisa). */
export const evaluacionesDelInforme = (d: DatosEmpresa, puestoId: string, tipo: TipoInforme) =>
  evaluacionesDeTipo(tipo, d.evaluacionesPorPuesto[puestoId] ?? []);

export async function entradasPuestos(d: DatosEmpresa): Promise<EntradaPuesto[]> {
  return Promise.all(d.puestos.map(async p => {
    const evaluaciones = d.evaluacionesPorPuesto[p.id] ?? [];
    const informes: EntradaPuesto['informes'] = {};
    for (const t of TIPOS_INFORME) {
      const inf = d.informes.find(i => i.job_position_id === p.id && i.kind === t);
      const evals = evaluacionesDeTipo(t, evaluaciones);
      const version = inf ? ultimaVersion(d.versiones.filter(v => v.report_id === inf.id)) : null;
      // GT 5: adjuntos de laboratorio de la versión aprobada (contenido congelado), no los del borrador.
      const laboratorio = ((version?.snapshot as { adjuntos?: { tipo?: string }[] } | undefined)?.adjuntos ?? []).filter(a => a.tipo === 'laboratorio').length;
      informes[t] = { borrador: !!inf, version, huella: await huellaEvaluaciones(evals), evals, laboratorio };
    }
    return { id: p.id, nombre: p.name, activos: d.activosPorPuesto[p.id] ?? 0, evaluaciones, informes };
  }));
}

// ── Sync del Checklist (GT 5, 6, 7, 15, 16, 17, 37 y TH 1) ──
export async function syncInformesPuesto(companyId: string, aplicables: Set<string>, hoy: Date = new Date()): Promise<TrazasPlanV2> {
  if (!companyId) return {};
  const res: Record<string, ResultadoItemLoteD> = {};
  // GT 5, 6 y 7: solo si la empresa ya usa los informes por puesto (sin M49 en la base, la consulta falla: nada).
  const { count, error } = await supabase.from('position_reports').select('id', { count: 'exact', head: true }).eq('company_id', companyId);
  let gt6ConMedidas = new Set<string>();
  if (!error && (count ?? 0) > 0) {
    const d = await cargarDatosEmpresa(companyId);
    const puestos = estadoPuestos(await entradasPuestos(d), hoy);
    for (const item of ITEMS_INFORMES) res[item] = resultadoItemInformes(item, puestos);
    // GT 9 (GT-14): solo con algún informe de medidas aprobado (si no, sigue Inspecciones v2 o el módulo anterior).
    const aprobadas = idsMedidasAprobadas(d);
    if (aprobadas) res[ITEM_VERIFICACION] = resultadoVerificacionGT9(aprobadas, medidasGT9(d), d.verificaciones, hoy);
    gt6ConMedidas = new Set(puestos.filter(p => p.resultado.riesgos.estado === 'vigente').map(p => p.id).filter(id => {
      const inf = d.informes.find(i => i.job_position_id === id && i.kind === 'riesgos');
      const v = inf ? ultimaVersion(d.versiones.filter(x => x.report_id === inf.id)) : null;
      const ap = (v?.snapshot as { atencion_prioritaria?: { medidas?: number } } | undefined)?.atencion_prioritaria;
      return (ap?.medidas ?? 0) > 0;
    }));
  }
  // GT 37: si la empresa tiene procedimientos (si no, sigue la regla anterior de Permisos de Trabajo).
  const procs = await lista<{ id: string; permit_type: string }>(supabase.from('special_work_procedures').select('id, permit_type').eq('company_id', companyId));
  if (procs.length) {
    const [permisos, versiones, socs] = await Promise.all([
      lista<PermisoMin>(supabase.from('work_permits').select('permit_type, valid_from, created_at, status').eq('company_id', companyId)),
      lista<FilaVersion>(supabase.from('sst_document_versions').select('id, procedure_id, version').eq('company_id', companyId).eq('doc_type', 'procedimiento')),
      lista<{ procedure_version_id: string | null; status: string }>(supabase.from('signed_records').select('procedure_version_id, status')
        .eq('company_id', companyId).eq('kind', 'socializacion')),
    ]);
    res[ITEM_PROCEDIMIENTOS] = resultadoProcedimientos(permisos, procs, versiones, socs, hoy);
  }
  // TH 1: conteos del servidor (admin y manager); sin declaraciones, nada.
  const { data: resumen, error: e2 } = await supabase.rpc('atencion_prioritaria_resumen');
  if (!e2 && resumen && (resumen as ResumenPrioritaria).declarados > 0) {
    res[ITEM_PRIORITARIA] = resultadoPrioritaria(resumen as ResumenPrioritaria, gt6ConMedidas);
  }
  return escribirLoteD(companyId, res, aplicables);
}

/** ¿Manda el cálculo de los informes en GT 9? (la empresa tiene algún informe de medidas aprobado) */
export async function informesMandanGT9(companyId: string): Promise<boolean> {
  if (!companyId) return false;
  const { count, error } = await supabase.from('sst_document_versions').select('id', { count: 'exact', head: true })
    .eq('company_id', companyId).eq('doc_type', 'informe_puesto').eq('report_kind', 'medidas');
  return !error && (count ?? 0) > 0;
}

/** ¿Manda el cálculo de procedimientos en GT 37? (la empresa tiene al menos uno) */
export async function usaProcedimientos(companyId: string): Promise<boolean> {
  const { count, error } = await supabase.from('special_work_procedures').select('id', { count: 'exact', head: true }).eq('company_id', companyId);
  return !error && (count ?? 0) > 0;
}

// ── Borradores, adjuntos y medidas ──
export async function crearBorrador(companyId: string, puestoId: string, tipo: TipoInforme, nombre: string): Promise<FilaInforme> {
  const { data, error } = await supabase.from('position_reports').insert({ company_id: companyId, job_position_id: puestoId, kind: tipo, position_name: nombre })
    .select('*');
  if (error) falla(error, 'crear el informe');
  return (data as FilaInforme[])[0];
}
export async function guardarBorrador(id: string, cambios: Pick<FilaInforme, 'conclusions' | 'recommendations'>): Promise<void> {
  const { data, error } = await supabase.from('position_reports').update(cambios).eq('id', id).select('id');
  if (error) falla(error, 'guardar el informe');
  if (!data?.length) throw new Error('No tienes permiso para guardar el informe.');
}

export const ACEPTA_ARCHIVO = 'application/pdf,image/png,image/jpeg';
export const MAX_ARCHIVO = 10 * 1024 * 1024;
export function validarArchivo(f: File): string | null {
  if (!['application/pdf', 'image/png', 'image/jpeg'].includes(f.type)) return 'El archivo debe ser PDF, PNG o JPEG.';
  if (f.size <= 0 || f.size > MAX_ARCHIVO) return 'El archivo debe pesar hasta 10 MB.';
  return null;
}
async function subir(ruta: string, f: Blob, tipo: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET_INFORMES).upload(ruta, f, { contentType: tipo, upsert: false });
  if (error) falla(error as { message?: string }, 'subir el archivo');
}
export async function urlArchivo(ruta: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_INFORMES).createSignedUrl(ruta, 120);
  if (error || !data?.signedUrl) throw new Error('No se pudo abrir el archivo. Intenta de nuevo.');
  return data.signedUrl;
}

export type DatosAdjunto = Pick<FilaAdjunto, 'kind' | 'title'> & Partial<Pick<FilaAdjunto, 'agent_type' | 'agent_name' | 'result_value' | 'limit_value'
  | 'unit' | 'limit_source' | 'measured_at' | 'provider' | 'equipment' | 'serial_number' | 'valid_until'>>;
export async function subirAdjunto(companyId: string, informeId: string, archivo: File, datos: DatosAdjunto): Promise<FilaAdjunto> {
  const e = validarArchivo(archivo);
  if (e) throw new Error(e);
  const id = crypto.randomUUID();
  const ruta = `${companyId}/informes/${informeId}/${id}-${aRutaSegura(archivo.name)}`;
  await subir(ruta, archivo, archivo.type);
  const { data, error } = await supabase.from('position_report_files').insert({ id, company_id: companyId, report_id: informeId, ...datos,
    file_path: ruta, file_name: archivo.name.slice(0, 200), file_mime: archivo.type, file_size: archivo.size }).select('*');
  if (error) falla(error, 'guardar el adjunto');
  return (data as FilaAdjunto[])[0];
}
export async function borrarAdjunto(id: string): Promise<void> {
  const { data, error } = await supabase.from('position_report_files').delete().eq('id', id).select('id');
  if (error) falla(error, 'borrar el adjunto');
  if (!data?.length) throw new Error('No tienes permiso para borrar el adjunto.');
}

export async function vincularMedida(companyId: string, puestoId: string, m: { origen: 'iper'; iper_id: string; nivel: string } | { origen: 'incidente'; accion_id: string }): Promise<void> {
  const fila: Record<string, unknown> = m.origen === 'iper'
    ? { company_id: companyId, job_position_id: puestoId, source: 'iper', iper_assessment_id: m.iper_id, control_level: m.nivel }
    : { company_id: companyId, job_position_id: puestoId, source: 'incidente', incident_action_id: m.accion_id };
  const { error } = await supabase.from('position_measures').insert(fila);
  if (error) falla(error, 'vincular la medida');
}
export async function crearMedidaPropia(companyId: string, puestoId: string, nivel: string, descripcion: string, prioritaria: boolean): Promise<void> {
  const { error } = await supabase.from('position_measures').insert({ company_id: companyId, job_position_id: puestoId, source: 'puesto',
    control_level: nivel, description: descripcion.trim(), for_priority_group: prioritaria });
  if (error) falla(error, 'agregar la medida');
}
export type SeguimientoMedida = Pick<FilaMedida, 'responsible_name' | 'due_date' | 'follow_up' | 'result' | 'status' | 'for_priority_group'>;
export async function actualizarMedida(m: FilaMedida, cambios: Partial<SeguimientoMedida>): Promise<void> {
  // Las de Incidentes llevan su seguimiento en Incidentes: aquí solo cambia si son para el grupo prioritario.
  const permitido = m.source === 'incidente' ? { for_priority_group: cambios.for_priority_group ?? m.for_priority_group } : cambios;
  const { data, error } = await supabase.from('position_measures').update(permitido).eq('id', m.id).select('id');
  if (error) falla(error, 'guardar la medida');
  if (!data?.length) throw new Error('No tienes permiso para cambiar la medida.');
}
export async function cerrarMedida(companyId: string, m: FilaMedida, evidencia: File, fecha: string, resultado: string): Promise<void> {
  const e = validarArchivo(evidencia);
  if (e) throw new Error(e);
  const ruta = `${companyId}/medidas/${m.id}/${crypto.randomUUID()}-${aRutaSegura(evidencia.name)}`;
  await subir(ruta, evidencia, evidencia.type);
  const { error } = await supabase.from('position_measures').update({ status: 'cerrada', closed_at: fecha, result: resultado.trim() || m.result,
    evidence_path: ruta, evidence_name: evidencia.name.slice(0, 200) }).eq('id', m.id);
  if (error) falla(error, 'cerrar la medida');
}
// ── Verificación in situ (GT 9): solo se registra; no se edita ni se borra ──
export interface DatosVerificacion { fecha: string; resultado: ResultadoVerificacion; evidencia: File; inspeccionId: string | null; notas: string }
export async function registrarVerificacion(companyId: string, medidaId: string, datos: DatosVerificacion): Promise<void> {
  const e = validarArchivo(datos.evidencia);
  if (e) throw new Error(e);
  const ruta = `${companyId}/verificaciones/${medidaId}/${crypto.randomUUID()}-${aRutaSegura(datos.evidencia.name)}`;
  await subir(ruta, datos.evidencia, datos.evidencia.type);
  const { error } = await supabase.from('position_measure_verifications').insert({ company_id: companyId, measure_id: medidaId,
    verified_on: datos.fecha, result: datos.resultado, evidence_path: ruta, evidence_name: datos.evidencia.name.slice(0, 200),
    inspection_id: datos.inspeccionId, notes: datos.notas.trim() || null });
  if (error) falla(error, 'registrar la verificación in situ');
}
export interface InspeccionFirmada { id: string; performed_date: string | null; area: string | null; signed_at: string }
/** Inspecciones firmadas de la empresa (para vincular una a la verificación; opcional). */
export async function inspeccionesFirmadas(companyId: string): Promise<InspeccionFirmada[]> {
  return lista<InspeccionFirmada>(supabase.from('insp_inspections').select('id, performed_date, area, signed_at').eq('company_id', companyId)
    .eq('status', 'completada').order('signed_at', { ascending: false }).limit(50));
}

export async function borrarMedida(id: string): Promise<void> {
  const { data, error } = await supabase.from('position_measures').delete().eq('id', id).select('id');
  if (error) falla(error, 'quitar la medida');
  if (!data?.length) throw new Error('No tienes permiso para quitar la medida.');
}

// ── Aprobar (informe o procedimiento): versión nueva e inmutable ──
export interface FirmaAprobacion { metodo: 'pantalla'; nombre: string; cedula: string; trazo: Blob }
export interface PapelAprobacion { metodo: 'papel'; escaneo: File }
export async function aprobarVersion(companyId: string, doc: { tipo: 'informe_puesto'; informeId: string; kind: TipoInforme; huella: string }
  | { tipo: 'procedimiento'; procedimientoId: string; permitType: TipoPermiso }
  | { tipo: 'procedimiento_epp'; procedimientoEppId: string }, snapshot: object, hash: string, firma: FirmaAprobacion | PapelAprobacion): Promise<FilaVersion> {
  const id = crypto.randomUUID();
  const fila: Record<string, unknown> = { id, company_id: companyId, doc_type: doc.tipo, version: 1, snapshot, content_hash: hash, sign_method: firma.metodo };
  if (doc.tipo === 'informe_puesto') Object.assign(fila, { report_id: doc.informeId, report_kind: doc.kind, eval_fingerprint: doc.huella });
  else if (doc.tipo === 'procedimiento') Object.assign(fila, { procedure_id: doc.procedimientoId, permit_type: doc.permitType });
  // M51 (PO 21): el procedimiento de adquisición de EPP, aprobado igual que los procedimientos de GT 37.
  else Object.assign(fila, { epp_procedure_id: doc.procedimientoEppId });
  if (firma.metodo === 'pantalla') {
    if (firma.nombre.trim().length < 3) throw new Error('Escribe el nombre de quien aprueba.');
    if (!cedulaValida(firma.cedula)) throw new Error('La cédula debe tener 10 dígitos.');
    const ruta = `${companyId}/versiones/${id}/firma.png`;
    await subir(ruta, firma.trazo, 'image/png');
    Object.assign(fila, { signer_name: firma.nombre.trim(), signer_cedula: firma.cedula.trim(), signature_path: ruta });
  } else {
    const e = validarArchivo(firma.escaneo);
    if (e) throw new Error(e);
    const ruta = `${companyId}/versiones/${id}/escaneo-${aRutaSegura(firma.escaneo.name)}`;
    await subir(ruta, firma.escaneo, firma.escaneo.type);
    fila.scan_path = ruta;
  }
  const { data, error } = await supabase.from('sst_document_versions').insert(fila).select('*');
  if (error) falla(error, 'aprobar la versión');
  return (data as FilaVersion[])[0];
}

// ── Procedimientos ──
export interface DatosProcedimientos {
  procedimientos: FilaProcedimiento[]; versiones: FilaVersion[]; preguntas: Record<string, PreguntaPermiso[]>;
  peligros: { hazard_type: string | null; hazard_description: string | null; possible_effects: string | null }[];
  socializaciones: { id: string; procedure_version_id: string | null; status: string; record_date: string }[];
  permisos: PermisoMin[]; puestos: PuestoBase[];
}
export async function cargarProcedimientos(companyId: string): Promise<DatosProcedimientos> {
  const { data: procs, error } = await supabase.from('special_work_procedures').select('*').eq('company_id', companyId);
  if (error) falla(error, 'leer los procedimientos');
  const [versiones, preguntas, peligros, socializaciones, permisos, puestos] = await Promise.all([
    lista<FilaVersion>(supabase.from('sst_document_versions').select('*').eq('company_id', companyId).eq('doc_type', 'procedimiento')),
    lista<PreguntaPermiso & { permit_type: string }>(supabase.from('work_permit_checklist_items').select('permit_type, item_text, display_order, referencia')),
    lista<DatosProcedimientos['peligros'][number]>(supabase.from('iper_assessments').select('hazard_type, hazard_description, possible_effects').eq('company_id', companyId)),
    lista<DatosProcedimientos['socializaciones'][number]>(supabase.from('signed_records').select('id, procedure_version_id, status, record_date')
      .eq('company_id', companyId).eq('kind', 'socializacion')),
    lista<PermisoMin>(supabase.from('work_permits').select('permit_type, valid_from, created_at, status').eq('company_id', companyId)),
    lista<PuestoBase>(supabase.from('job_positions').select('id, name').eq('company_id', companyId).order('name')),
  ]);
  const porTipo: Record<string, PreguntaPermiso[]> = {};
  preguntas.forEach(p => { (porTipo[p.permit_type] ??= []).push(p); });
  return { procedimientos: (procs ?? []) as FilaProcedimiento[], versiones, preguntas: porTipo, peligros, socializaciones, permisos, puestos };
}
export async function guardarProcedimiento(companyId: string, existente: FilaProcedimiento | null, tipo: TipoPermiso,
  datos: Omit<FilaProcedimiento, 'id' | 'company_id' | 'permit_type' | 'created_at' | 'updated_at'>): Promise<FilaProcedimiento> {
  const q = existente ? supabase.from('special_work_procedures').update(datos).eq('id', existente.id).select('*')
    : supabase.from('special_work_procedures').insert({ ...datos, company_id: companyId, permit_type: tipo }).select('*');
  const { data, error } = await q;
  if (error) falla(error, 'guardar el procedimiento');
  if (!data?.length) throw new Error('No tienes permiso para guardar el procedimiento.');
  return (data as FilaProcedimiento[])[0];
}

/** Contenido de la socialización (registro firmado de M45 con el tipo socializacion). */
export function contenidoSocializacion(version: Pick<FilaVersion, 'id' | 'version'>, nombreProc: string, empresa: string, fecha: string,
  quien: string, material: string, asistentes: { id: string; full_name: string; cedula: string | null }[]): ContenidoSocializacion {
  return {
    tipo: 'socializacion', version_id: version.id, empresa, fecha, titulo: `${nombreProc} (versión ${version.version})`, tema: `Procedimiento de ${nombreProc.toLowerCase()}`,
    tecnico: quien.trim(), material: material.trim(), evaluacion: '', duracion_horas: null,
    asistentes: [...asistentes].sort((a, b) => a.full_name.localeCompare(b.full_name, 'es') || a.id.localeCompare(b.id))
      .map(a => ({ worker_id: a.id, nombre: a.full_name.trim(), cedula: (a.cedula ?? '').trim(), evaluacion: null, nota: null })),
  };
}
export const esSocializacion = conLista;

/** Imagen de la firma (PNG) como data URL, para el PDF. */
export async function dataUrlArchivo(ruta: string | null): Promise<string | null> {
  if (!ruta) return null;
  const { data, error } = await supabase.storage.from(BUCKET_INFORMES).download(ruta);
  if (error || !data) return null;
  return new Promise(resolve => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result as string);
    lector.onerror = () => resolve(null);
    lector.readAsDataURL(data);
  });
}

/** Datos de la aprobación para el PDF (con el trazo de la firma). */
export async function aprobacionParaPdf(v: FilaVersion) {
  return { version: v.version, metodo: v.sign_method, nombre: v.signer_name, cedula: v.signer_cedula, aprobadoEn: v.approved_at, hash: v.content_hash,
    trazo: v.sign_method === 'pantalla' ? await dataUrlArchivo(v.signature_path) : null };
}

/** Conteos de atención prioritaria por puesto (solo admin y manager; sin nombres ni tipo). null si no se puede leer. */
export async function resumenPrioritaria(): Promise<ResumenPrioritaria | null> {
  try {
    const { data, error } = await supabase.rpc('atencion_prioritaria_resumen');
    return error || !data ? null : (data as ResumenPrioritaria);
  } catch {
    return null;
  }
}
